import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolSpineAnimation } from '@/containers/symbols/SymbolSpineAnimation'
import { SymbolType } from '@/containers/symbols/SymbolType'
import Facade from '@/Facade'
import { SymbolOutOverlayAnimationPool, SymbolsAnimationPool } from '@/factories/Factory'
import { IPoolable } from '@base/pool/IPoolable'
import gsap from 'gsap'
import type { TrackEntry as ITrackEntry } from '@esotericsoftware/spine-pixi-v8'
import * as PIXI from 'pixi.js'
import { IAnimationWrapper, NewSymbolData, SymbolContainerData } from '@/modules'
import { ScalingTextField } from '@/containers/common/ScalingTextField'
import { MULTIPLIER_VALUE_STYLE } from '@/containers/common/TextStyles'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import GameSignals from '@/signals/GameSignals'
import { BitmapTextH } from '@/utils/pixi-heaven-compat'

export class SymbolContainer extends PIXI.Container implements IPoolable {
    private static SHADOW_IN_DEFAULT_TINT_POWER: number
    private static readonly BONUS_SYMBOL_EXTRA_SCALE: number = 1.3
    // Backup switch: set to false to instantly restore legacy behavior
    private static readonly USE_DYNAMIC_SYMBOL_ANIMATION_FIT: boolean = true
    // Legacy scale kept as backup/reference
    private static readonly LEGACY_SYMBOL_ANIMATION_SCALE: number = 1.2
    private static readonly AVAILABLE_MULTIPLIER_TEXTURES: number[] = [2, 3, 4, 5, 6, 8, 10, 15, 20, 25, 50, 100]
    private static readonly SYMBOL_OUT_TARGET_TIMESCALE: number = 19 / 14
    private static readonly SYMBOL_OUT_START_OFFSET_SECONDS: number = 0.08
    private _symbolData: SymbolContainerData
    private _tintPower: number
    private readonly _staticImage: PIXI.Sprite
    private readonly _animationWrapper: PIXI.Container
    private rect: PIXI.Graphics
    private _animation: SymbolSpineAnimation
    private _animationCompleteCallback: () => void
    private _animationTrack: ITrackEntry
    private readonly _multiplierValue: ScalingTextField
    private readonly _multiplierValueImage: PIXI.Sprite
    private readonly _animationMultiplierValue: ScalingTextField
    private _hpOutOverlayWrapper: PIXI.Container | null

    public constructor() {
        super()
        SymbolContainer.SHADOW_IN_DEFAULT_TINT_POWER = Facade.symbolsConfig.SHADOW_OPACITY

        this._staticImage = new PIXI.Sprite()
        this._staticImage.anchor.set(0.5)
        this._staticImage.interactive = false

        this.addChild(this._staticImage)
        // const rect = new PIXI.Graphics()
        // // rect.x = -58
        // // rect.y = -53
        // rect.beginFill(0, 0.001)
        // rect.lineStyle(1, 0xff0000)
        // rect.drawRect(5, 5, 100, 100)

        // rect.interactive = true
        // rect.buttonMode = true
        // this.addChild(rect)

        this._animationWrapper = new PIXI.Container()
        this._animationWrapper.visible = false
        this.addChild(this._animationWrapper)

        this._multiplierValue = new ScalingTextField(0.6, 100, BitmapTextH, MULTIPLIER_VALUE_STYLE)
        this._multiplierValue.text = ''
        this._multiplierValue.position.set(-1, 28)
        this.addChild(this._multiplierValue)

        this._multiplierValueImage = new PIXI.Sprite()
        this._multiplierValueImage.anchor.set(0.5)
        this._multiplierValueImage.position.set(0, 0)
        this._multiplierValueImage.visible = false
        this.addChild(this._multiplierValueImage)

        this._animationMultiplierValue = new ScalingTextField(0.6, 100, BitmapTextH, MULTIPLIER_VALUE_STYLE)
        this._animationMultiplierValue.text = ''
        this._animationMultiplierValue.scale.set(1 / 0.85)
        this._animationMultiplierValue.position.set(1, -14)
        this._hpOutOverlayWrapper = null

        this._symbolData = null
        this._tintPower = 0
        // this.rect = new PIXI.Graphics()
        // this.rect.x = -58
        // this.rect.y = -53
        // this.rect.beginFill(0, 0.001)
        // this.rect.lineStyle(1, 0xff0000)
        // this.rect.drawRect(5, 5, 200, 200)
        // this.rect.interactive = true
        // this.rect.buttonMode = true
        // this.addChild(this.rect)
    }

    public init(data: NewSymbolData) {
        this.reset()
        this._symbolData = data.symbolData

        const resolveTextureName = (): string => {
            const indexes = Facade.symbolsConfig?.indexes || {}

            const requestedIndex = Number(this._symbolData?.index)

            const resolvedIndex = requestedIndex

            const candidate = indexes?.[resolvedIndex]

            if (typeof candidate === 'string') {
                return candidate
            }

            if (candidate && typeof candidate === 'object') {
                const candidateObj = candidate as Record<string, unknown>
                const objectFallback =
                    candidateObj.texture ||
                    candidateObj.static ||
                    candidateObj.idle ||
                    candidateObj.normal ||
                    candidateObj.default

                if (typeof objectFallback === 'string') {
                    return objectFallback
                }
            }

            const safeFallback = indexes?.[SpecialSymbolType.MULTIPLIER]
            return typeof safeFallback === 'string' ? safeFallback : ''
        }

        const imageTextureName: string = resolveTextureName()
        this._staticImage.texture = PIXI.Texture.from(imageTextureName)
        this.setImageScale(this._symbolData.index)
        this._staticImage.name = 'STATIC'
        this._multiplierValue.visible = false
        this._multiplierValue.text = ''
        this._animationMultiplierValue.text = `${data.symbolData.multiplier}x`

        if (this.index === SpecialSymbolType.MULTIPLIER) {
            const textureName = `x${data.symbolData.multiplier}.png`
            const texture = PIXI.Texture.from(textureName)
            const hasTexture = !!texture && texture !== PIXI.Texture.EMPTY

            this._multiplierValueImage.visible = hasTexture
            this._multiplierValueImage.texture = hasTexture ? texture : PIXI.Texture.EMPTY

            if (hasTexture) {
                const texW = texture.orig?.width ?? texture.width ?? 1
                const texH = texture.orig?.height ?? texture.height ?? 1
                const targetW = Facade.symbolsConfig.WIDTH * 0.78
                const targetH = Facade.symbolsConfig.HEIGHT * 0.72
                const fitScale = Math.min(targetW / texW, targetH / texH)

                this._multiplierValueImage.scale.set(fitScale * 2)
            }
        } else {
            this._multiplierValueImage.visible = false
            this._multiplierValueImage.texture = PIXI.Texture.EMPTY
        }

        this.detachAnimation()
    }
    protected onClick(rect: any, staticImg: any) {
        if (rect.parent.name === String(SpecialSymbolType.SCATTER)) {
            GameSignals.showBigDescripion.emit(2221)
        } else {
            GameSignals.showSmallDescripion.emit(2221)
        }
        //staticImg.visible = !staticImg.visible

        // image.scale.set(0.5)
    }
    protected setImageScale = (symbolId: number) => {
        // this.rect.interactive = true
        // this.rect.buttonMode = true

        this._staticImage.anchor.set(0.5)

        const texture = this._staticImage.texture
        const textureWidth = texture?.orig?.width ?? texture?.width ?? 0
        const textureHeight = texture?.orig?.height ?? texture?.height ?? 0

        if (textureWidth > 0 && textureHeight > 0) {
            const targetWidth = Facade.symbolsConfig.WIDTH
            const targetHeight = Facade.symbolsConfig.HEIGHT
            const fitScale = Math.min(targetWidth / textureWidth, targetHeight / textureHeight) * 0.99
            const staticMultiplierScaleBoost = symbolId === SpecialSymbolType.MULTIPLIER ? 1.18 : 1
            this._staticImage.scale.set(
                symbolId === SpecialSymbolType.SCATTER
                    ? fitScale * SymbolContainer.BONUS_SYMBOL_EXTRA_SCALE
                    : fitScale * staticMultiplierScaleBoost
            )
        } else {
            this._staticImage.scale.set(
                symbolId === SpecialSymbolType.SCATTER
                    ? SymbolContainer.BONUS_SYMBOL_EXTRA_SCALE
                    : symbolId === SpecialSymbolType.MULTIPLIER
                      ? 1.18
                      : 1
            )
        }
        //this.rect.on('pointerdown', this.onClick.bind(this, this.rect, this._staticImage))
    }

    public reset() {
        gsap.killTweensOf(this, 'tintPower')
        this.detachAnimation()
        this.clearHpOutOverlayAnimation()

        this._symbolData = null
        this._staticImage.tint = 0xffffff
        this._multiplierValue.content.tint = 0xffffff
        this._multiplierValueImage.tint = 0xffffff
        this._tintPower = 0

        this.filters = null
        this.visible = true

        this._multiplierValue.text = ''
        this._multiplierValue.visible = false
        this._multiplierValueImage.visible = false
        this._multiplierValueImage.texture = PIXI.Texture.EMPTY
        this._animationMultiplierValue.text = ''
        this.scale.set(1)
        this._animationMultiplierValue.maxWidth = 100
    }

    public playHpOutOverlayAnimation = (loop: boolean = false) => {
        this.clearHpOutOverlayAnimation()

        const wrapper: PIXI.Container = new PIXI.Container() as IAnimationWrapper
        wrapper.position.set(0, 0)
        this.addChild(wrapper)
        this._hpOutOverlayWrapper = wrapper

        const animation: SpineAnimation = SymbolOutOverlayAnimationPool.out(wrapper, { name: 'symbol_out' })
        if (!animation) {
            console.warn('[HP_OUT_OVERLAY] animation instance not available', { index: this._symbolData?.index })
            this.clearHpOutOverlayAnimation()
            return
        }
        ;(wrapper as IAnimationWrapper).animation = animation
        animation.scale.set(1)

        try {
            animation.start(
                ['animation'],
                [loop],
                0,
                [SymbolContainer.SYMBOL_OUT_TARGET_TIMESCALE],
                () => {
                    if (!loop) {
                        this.clearHpOutOverlayAnimation()
                    }
                },
                null,
                SymbolContainer.SYMBOL_OUT_START_OFFSET_SECONDS
            )
        } catch (error) {
            console.warn('[HP_OUT_OVERLAY] failed to start', {
                index: this._symbolData?.index,
                error,
            })
            this.clearHpOutOverlayAnimation()
        }
    }

    public stopHpOutOverlayAnimation = () => {
        this.clearHpOutOverlayAnimation()
    }

    private clearHpOutOverlayAnimation = () => {
        if (!this._hpOutOverlayWrapper) {
            return
        }

        const wrapper = this._hpOutOverlayWrapper as IAnimationWrapper
        const animation = wrapper.animation as SpineAnimation

        if (animation) {
            animation.stop()
            wrapper.removeChild(animation)
            SymbolOutOverlayAnimationPool.in(animation)
        }

        if (wrapper.parent) {
            wrapper.parent.removeChild(wrapper)
        }

        this._hpOutOverlayWrapper = null
    }

    public playAnimation(
        state: string | string[],
        loop: boolean | boolean[] = false,
        callback: () => void = null,
        allowMultiplierLanding: boolean = false
    ) {
        const states: string[] = Array.isArray(state) ? state : [state]
        const hasLandingState = states.includes('land')

        // Safety: multiplier symbol should stay static during generic reel landing.
        if (this.index === SpecialSymbolType.MULTIPLIER && hasLandingState && !allowMultiplierLanding) {
            if (callback) {
                callback()
            }
            return
        }

        this.attachAnimation()

        if (this._animation != null) {
            this._animationCompleteCallback = null
            this.removeAnimationTrack()

            this._animation.stop()

            if (!states?.length) {
                if (callback) {
                    callback()
                }
                return
            }

            const loops: boolean[] = Array.isArray(loop) ? loop : [loop]

            const firstTrack = this._animation.play(states[0], loops[0] ?? false)
            let lastTrack = firstTrack

            for (let index = 1; index < states.length; index++) {
                const queuedTrack = this._animation.queue(states[index], loops[index] ?? false)
                if (queuedTrack) {
                    lastTrack = queuedTrack
                }
            }

            this._animationTrack = lastTrack
            this.setAnimationPosition()

            if (this._animationTrack == null) {
                this.detachAnimation()
                if (callback) {
                    callback()
                }
                return
            }

            this._animationCompleteCallback = callback
            this._animationTrack.listener = {
                complete: this.onCompleteAnimation,
                interrupt: this.onCompleteAnimation,
                end: this.onCompleteAnimation,
            }
        }
    }

    private getMultiplierStaticTexture(multiplier: number, fallbackTextureName: string): PIXI.Texture {
        const normalized = SymbolContainer.AVAILABLE_MULTIPLIER_TEXTURES.includes(Number(multiplier))
            ? Number(multiplier)
            : 2
        const candidate = PIXI.Texture.from(`x${normalized}.png`)

        if (candidate && candidate !== PIXI.Texture.EMPTY) {
            return candidate
        }

        return PIXI.Texture.from(fallbackTextureName)
    }

    public stopAnimation() {
        if (this.animation) {
            this.animation.state.clearListeners() // TODO isn't beautiful
        }
        this.removeAnimationTrack()
        this.completeAnimation()
        this.detachAnimation()
    }

    public shadowIn(animate: boolean = true, power: number = -1) {
        gsap.killTweensOf(this, 'tintPower')

        if (power === -1) {
            power = SymbolContainer.SHADOW_IN_DEFAULT_TINT_POWER
        }

        if (animate) {
            gsap.to(this, { duration: 0.5, tintPower: power })
        } else {
            this.tintPower = power
        }
    }

    public shadowOut(animate: boolean = true) {
        gsap.killTweensOf(this, 'tintPower')

        if (animate) {
            gsap.to(this, { duration: 0.5, tintPower: 0 })
        } else {
            this.tintPower = 0
        }
    }

    protected attachAnimation() {
        if (this._animation == null) {
            this._animation = SymbolsAnimationPool.out(this._animationWrapper, this._symbolData)

            if (this._animation != null) {
                this._animation.state.listeners = []

                // Ensure pooled animations do not keep stale tint/light from previous symbol usages.
                const brightness = 1.0 - this._tintPower
                const channel = Math.max(0, Math.min(255, Math.round(brightness * 255)))
                const tintColor = (channel << 16) | (channel << 8) | channel

                if ((this._animation as any)?.color?.setLight) {
                    ;(this._animation as any).color.setLight(brightness, brightness, brightness)
                }
                this._animation.tint = tintColor

                this._animationWrapper.visible = true
                this._staticImage.visible = false
            }
        }
    }

    protected setAnimationPosition() {
        const symbolAnimationScale = SymbolContainer.USE_DYNAMIC_SYMBOL_ANIMATION_FIT
            ? this.getDynamicAnimationScale()
            : SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE
        this._animation.position.set(0, 0)
        this._animation.scale.set(symbolAnimationScale)

        switch (this.index) {
            case SymbolType.LP_1:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(6, -4)
                break
            case SymbolType.LP_2:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(0, -2)
                break
            case SymbolType.LP_3:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(0, -3)
                break
            case SymbolType.LP_4:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(0, -4)
                break
            case SymbolType.LP_5:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(2, 2)
                break
            case SymbolType.HP_4:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(1, -3)
                break
            case SymbolType.HP_3:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(0, 12)
                break
            case SymbolType.HP_2:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(0.5, 9)
                break
            case SymbolType.HP_1:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(-1, 7)
                break
            case SpecialSymbolType.SCATTER:
                //this._animation.position.set(-0.5, -5.5)
                this._animation.scale.set(
                    Math.max(symbolAnimationScale, SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE) *
                        SymbolContainer.BONUS_SYMBOL_EXTRA_SCALE
                )
                break
            case SpecialSymbolType.MULTIPLIER:
                this._animation.scale.set(symbolAnimationScale, symbolAnimationScale)
                //this._animation.position.set(-2, -5)
                break
        }
    }

    private getDynamicAnimationScale(): number {
        if (!this._animation) {
            return SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE
        }

        const targetWidth = Facade.symbolsConfig.WIDTH
        const targetHeight = Facade.symbolsConfig.HEIGHT

        let boundsWidth = 0
        let boundsHeight = 0

        try {
            // Ensure transform/bounds are up to date before measuring
            this._animation.update(0)
            const bounds = this._animation.getLocalBounds()
            boundsWidth = bounds?.width ?? 0
            boundsHeight = bounds?.height ?? 0
        } catch (error) {
            return SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE
        }

        if (boundsWidth <= 0 || boundsHeight <= 0) {
            return SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE
        }

        const fitScale = Math.min(targetWidth / boundsWidth, targetHeight / boundsHeight) * 0.99

        if (!Number.isFinite(fitScale) || fitScale <= 0) {
            return SymbolContainer.LEGACY_SYMBOL_ANIMATION_SCALE
        }

        return fitScale
    }

    protected removeAnimationTrack() {
        if (this._animationTrack != null) {
            this._animationTrack.listener = null
            this._animationTrack = null
        }
    }

    protected detachAnimation() {
        if (this._animation != null) {
            const animation = this._animation

            this._animationCompleteCallback = null
            this.removeAnimationTrack()

            setTimeout(() => {
                // sprite position shifting, if called without timeout
                SymbolsAnimationPool.in(animation)
                if ((animation as any)?.color?.setLight) {
                    ;(animation as any).color.setLight(1, 1, 1)
                }
                this._animationWrapper.visible = false
                this._staticImage.visible = true
            })

            this._animation = null
        }
    }

    protected onCompleteAnimation = (track: ITrackEntry) => {
        if (this._animationTrack === track) {
            if (track.loop) {
                this.completeAnimation()
            } else {
                this.stopAnimation()
            }
        }
    }

    protected completeAnimation() {
        if (this._animationCompleteCallback != null) {
            this._animationCompleteCallback()
            this._animationCompleteCallback = null
        }
    }

    public set tintPower(value: number) {
        if (this._tintPower === value) {
            return
        }

        const brightness = 1.0 - value
        const channel = Math.max(0, Math.min(255, Math.round(brightness * 255)))
        const tintColor = (channel << 16) | (channel << 8) | channel

        this._staticImage.tint = tintColor
        this._multiplierValueImage.tint = tintColor
        this._tintPower = value

        if (this._animation != null) {
            if ((this._animation as any)?.color?.setLight) {
                ;(this._animation as any).color.setLight(brightness, brightness, brightness)
            }
            this._animation.tint = tintColor
        }
    }

    public get tintPower(): number {
        return this._tintPower
    }

    public get index(): number {
        return this._symbolData.index
    }

    public get data() {
        return this._symbolData
    }

    public get animation() {
        return this._animation
    }

    public showMultiplierLandAnimation = (onComplete?: () => void) => {
        const restoreMultiplierStaticValueVisibility = this._multiplierValueImage.visible
        this._multiplierValueImage.visible = false

        let finished = false
        let fallbackTween: gsap.core.Tween | null = null

        const finish = () => {
            if (finished) {
                return
            }

            finished = true

            if (fallbackTween) {
                fallbackTween.kill()
                fallbackTween = null
            }

            this._multiplierValueImage.visible = restoreMultiplierStaticValueVisibility

            if (onComplete) {
                onComplete()
            }
        }

        this.playAnimation('land', false, finish, true)

        // Fallback: alcuni asset/stati spine possono non emettere complete in modo affidabile.
        fallbackTween = gsap.delayedCall(0.9, finish)

        if (this._animation?.replaceInSlotContainer) {
            this._animation.replaceInSlotContainer('number', this._animationMultiplierValue)
        }
    }

    public playLandingLoopOverlayOnStatic = () => {
        this.attachAnimation()

        if (!this._animation) {
            return
        }

        this._animationCompleteCallback = null
        this.removeAnimationTrack()
        this._animation.stop()

        this._animationTrack = this._animation.play('land', true)
        this.setAnimationPosition()

        // Overlay mode for test: keep static visible and play landing loop above it.
        this._staticImage.visible = true
        this._animationWrapper.visible = true
    }

    public getCurrentTrackDuration = (): number => {
        const track = (this._animation as any)?.state?.tracks?.[0]
        const duration = track?.animation?.duration

        if (typeof duration === 'number' && Number.isFinite(duration) && duration > 0) {
            return duration
        }

        return 0
    }
}
